/**
 * AncestorAvatar
 * --------------
 * A polite fallback for family-tree member photos that respects the
 * user's brief:
 *
 *   1. If a real photo is stored on the member → show it.
 *   2. If the member is deceased AND has no photo → render a
 *      gendered vintage silhouette (male/female/unknown) so the
 *      ancestor still has a face on the tree, plus an inline
 *      "Add photo" chip when the viewer is allowed to attach one.
 *   3. If the member is living AND has no photo → fall back to the
 *      first-letter initial (existing behaviour across the codebase).
 *
 * Zero external files — the silhouettes are inline SVGs so the tree
 * stays fast even with 300+ ancestors rendered simultaneously.
 */
import { resolveImage } from "@/lib/api";
import { ImagePlus } from "lucide-react";

/**
 * Vintage-portrait silhouette. Ovals with soft period-appropriate
 * shoulders — deliberately abstract so it doesn't imply a specific
 * ethnicity or era.
 */
function MaleSilhouette({ className, style }) {
  return (
    <svg viewBox="0 0 100 100" className={className} style={style} aria-hidden="true">
      <defs>
        <linearGradient id="anc-male-bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#EEF2FF" />
          <stop offset="1" stopColor="#C7D2FE" />
        </linearGradient>
      </defs>
      <rect width="100" height="100" fill="url(#anc-male-bg)" />
      {/* Shoulders / bust */}
      <path d="M12,102 C14,80 30,72 50,72 C70,72 86,80 88,102 Z" fill="#4C51BF" opacity="0.85" />
      {/* Collar hint */}
      <path d="M35,74 L50,88 L65,74 L58,72 L50,80 L42,72 Z" fill="#312E81" opacity="0.7" />
      {/* Head */}
      <ellipse cx="50" cy="45" rx="18" ry="22" fill="#4C51BF" opacity="0.9" />
      {/* Hair line */}
      <path d="M32,38 C34,26 66,26 68,38 L68,44 C64,32 36,32 32,44 Z" fill="#1E1B4B" opacity="0.6" />
    </svg>
  );
}

function FemaleSilhouette({ className, style }) {
  return (
    <svg viewBox="0 0 100 100" className={className} style={style} aria-hidden="true">
      <defs>
        <linearGradient id="anc-female-bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#FDF2F8" />
          <stop offset="1" stopColor="#FBCFE8" />
        </linearGradient>
      </defs>
      <rect width="100" height="100" fill="url(#anc-female-bg)" />
      {/* Shoulders / bust */}
      <path d="M8,104 C12,80 30,72 50,72 C70,72 88,80 92,104 Z" fill="#BE185D" opacity="0.85" />
      {/* Head */}
      <ellipse cx="50" cy="46" rx="18" ry="22" fill="#BE185D" opacity="0.9" />
      {/* Hair — softer, framing the face */}
      <path d="M30,42 C28,26 72,26 70,42 C74,54 74,66 68,74 C64,60 60,58 50,58 C40,58 36,60 32,74 C26,66 26,54 30,42 Z" fill="#831843" opacity="0.55" />
      {/* Earring hint */}
      <circle cx="32" cy="52" r="1.6" fill="#F9A8D4" opacity="0.9" />
      <circle cx="68" cy="52" r="1.6" fill="#F9A8D4" opacity="0.9" />
    </svg>
  );
}

function UnknownSilhouette({ className, style }) {
  return (
    <svg viewBox="0 0 100 100" className={className} style={style} aria-hidden="true">
      <defs>
        <linearGradient id="anc-unknown-bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#F5F5F4" />
          <stop offset="1" stopColor="#D6D3D1" />
        </linearGradient>
      </defs>
      <rect width="100" height="100" fill="url(#anc-unknown-bg)" />
      <path d="M12,102 C14,80 30,72 50,72 C70,72 86,80 88,102 Z" fill="#57534E" opacity="0.75" />
      <ellipse cx="50" cy="45" rx="18" ry="22" fill="#57534E" opacity="0.85" />
    </svg>
  );
}

function pickSilhouette(gender) {
  const g = (gender || "").toString().toLowerCase();
  if (g.startsWith("m")) return MaleSilhouette;
  if (g.startsWith("f")) return FemaleSilhouette;
  return UnknownSilhouette;
}

/**
 * Determines whether the member is deceased. Accepts either an
 * explicit `current_status` ("deceased") or the presence of a
 * `death_date` field.
 */
export function isDeceased(member) {
  if (!member) return false;
  if ((member.current_status || "").toLowerCase() === "deceased") return true;
  if (member.death_date) return true;
  return false;
}

/**
 * The main avatar component.
 *
 * Props
 * -----
 *  member         — the family-member document
 *  size           — pixel size of the round avatar (default 96)
 *  className      — extra classes on the wrapper
 *  onAddPhoto     — optional callback: when set + no photo exists,
 *                   renders a small "Add photo" hover chip.
 *  showAddChip    — pass false to hide the "Add photo" chip even
 *                   when onAddPhoto is set (e.g. inside the tree
 *                   diagram where the chip would overflow).
 */
export default function AncestorAvatar({
  member,
  size = 96,
  className = "",
  onAddPhoto,
  showAddChip = true,
}) {
  const rawPhoto = member?.photo;
  const photo = rawPhoto ? resolveImage(rawPhoto) : "";
  const dead = isDeceased(member);
  const Silhouette = pickSilhouette(member?.gender);

  const initial = (
    (member?.first_name || member?.last_name || "?").trim().charAt(0) || "?"
  ).toUpperCase();

  return (
    <div
      className={`relative overflow-hidden rounded-full bg-white/60 group ${className}`}
      style={{ width: size, height: size }}
      data-testid="ancestor-avatar"
      data-photo-present={photo ? "true" : "false"}
      data-deceased={dead ? "true" : "false"}
    >
      {photo ? (
        <img
          src={photo}
          alt={member?.first_name || ""}
          className="absolute inset-0 w-full h-full object-cover"
          draggable="false"
        />
      ) : dead ? (
        <Silhouette className="absolute inset-0 w-full h-full" />
      ) : (
        <div
          className="absolute inset-0 flex items-center justify-center font-display bg-gradient-to-br from-indigo-50 to-blue-50 text-indigo-800"
          style={{ fontSize: Math.max(14, Math.floor(size * 0.4)) }}
        >
          {initial}
        </div>
      )}

      {/* Deceased marker — the discreet dagger that already exists elsewhere. */}
      {dead ? (
        <div
          className="absolute rounded-full bg-black/70 text-white flex items-center justify-center font-serif leading-none"
          style={{
            top: Math.max(2, Math.floor(size * 0.03)),
            right: Math.max(2, Math.floor(size * 0.03)),
            width: Math.max(12, Math.floor(size * 0.18)),
            height: Math.max(12, Math.floor(size * 0.18)),
            fontSize: Math.max(9, Math.floor(size * 0.16)),
          }}
          aria-label="deceased"
          title="Deceased"
        >
          †
        </div>
      ) : null}

      {/* "Add photo" hover chip — only for deceased members with no photo, and only when the caller wired an onAddPhoto handler. */}
      {!photo && dead && onAddPhoto && showAddChip ? (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onAddPhoto(member);
          }}
          data-testid="ancestor-add-photo"
          className="absolute inset-x-0 bottom-0 h-8 bg-black/60 text-white text-[11px] font-medium flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <ImagePlus className="w-3 h-3" />
          Add photo
        </button>
      ) : null}
    </div>
  );
}
